import { cn } from "@/lib/utils";
import { isFlatShell } from "@/lib/shellFlat";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PROJECT_MENU_ITEMS, navChildLabel } from "@shared/navigationMenu";
import { useIsHighRise } from "@/hooks/useBuildingTerm";
import { useLocation } from "wouter";
import { BriefcaseBusiness, ChevronRight, LayoutGrid, MonitorCog, type LucideIcon } from "lucide-react";
import { useCompanyNav, useGlobalNav } from "@/hooks/useProjectNav";
import { adminSectionFor, companySectionFor, isAdminPath, isCompanyPath } from "@/lib/platformLayer";
import { useUIContext } from "@/contexts/UIContext";
import { useAuth } from "@/hooks/useAuth";
import { normalizeLocation } from "@/lib/utils";
import { Link } from "wouter";
import { useTranslation } from "@/hooks/useTranslation";
import { canAccessCompanySettings, canAccessPlatformSettings } from "@/lib/platformAccess";
import { usePublishedPageTitle } from "@/lib/pageTitle";

export function Breadcrumbs() {
  const t = useTranslation();
  const [location] = useLocation();
  const { mode, projectName } = useUIContext();
  const { user } = useAuth();
  const isHighRise = useIsHighRise();
  
  // Check if user has admin permissions
  const isAdmin = canAccessPlatformSettings(user);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  // Check if user is a salesperson (group_id 3) — userGroupId is from users table
  const groupId = (user as any)?.groupId ?? (user as any)?.userGroupId;
  const isSalesperson = groupId === '3' || groupId === 3;
  // Normalize location to handle trailing slashes, query strings, and hash fragments
  const normalizedLoc = normalizeLocation(location);
  const isOnAdminHome = normalizedLoc === '/admin' || normalizedLoc === '/home';
  const shouldHideBreadcrumb = isSuperAdmin && isOnAdminHome;
  /* The name the open page's own header shows. The step you are on takes it,
     so the crumb and the header below it can never call one page two things
     (they did on Offers, Project Settings and Legacy Integration). */
  const pageTitle = usePublishedPageTitle(normalizedLoc);
  
  // Parse breadcrumbs from the current route
  const getBreadcrumbs = () => {
    // The first crumb is PropPay's project grid: it goes to the Projects page
    // — the same glyph as Switch Project and the tab bar — not a house.
    const crumbs = [{ label: t("projectsTitle", "Projects"), path: "/projects" }];
    
    // On the home page and the Projects page the grid says it alone — the page
    // header names where you are right below it.
    if (normalizedLoc === '/home' || normalizedLoc === '/projects') {
      return crumbs;
    }
    
    if (mode === 'global' && isAdmin && !shouldHideBreadcrumb) {
      crumbs.push({ label: t("salefishAdmin", "SaleFish Admin"), path: "/admin" });
      
      // Add specific admin section if applicable
      if (location.startsWith('/admin/')) {
        const section = location.split('/')[2];
        const sectionLabels: Record<string, string> = {
          'settings': t("platformSettingsTitle", "Platform Settings") || 'Platform Settings',
          'companies': t("adminCompanies", "Companies"),
          'billing': t("adminBilling", "Billing & Proposals"),
          'integrations': t("integrations", "Integrations") || 'Integrations',
          'roles': t("roles", "Roles") || 'Roles',
          'users': t("adminUsers", "Users"),
          'usage': t("shellNavUsage", "Usage"),
          'competitor-analysis': t("shellNavCompetitorAnalysis", "Competitor Analysis"),
        };
        if (section && sectionLabels[section]) {
          crumbs.push({ label: sectionLabels[section], path: location });
        }
      }
    } else if (mode === 'project' && projectName) {
      // For salespeople, don't show project name - just home icon is enough
      if (!isSalesperson) {
        crumbs.push({ label: projectName, path: `/projects/${location.split('/')[2]}` });
        
        // Add specific project section if applicable
        if (location.split('/').length > 3) {
          const section = location.split('/')[3];
          /* Every sidebar item, by its first path segment, so a page the
             sidebar can reach is named in the trail. A hand-kept list of six
             left Offers, Sales List, Signing Queue, Email Marketing and
             Sales Management with no crumb of their own while their
             neighbours had one. The pages the sidebar does not list — the
             matrix, campaigns, inventory, the sync — are named here. */
          const sectionLabels: Record<string, string> = Object.fromEntries(
            PROJECT_MENU_ITEMS.map((item) => [
              item.url.split('/')[0],
              item.labelKey ? t(item.labelKey, item.label) : item.label,
            ]),
          );
          Object.assign(sectionLabels, {
            'settings': t("settings", "Settings") || 'Settings',
            'campaigns': t("campaignsTab", "Campaigns"),
            'inventory': t("inventory", "Inventory"),
            'integrations': t("integrations", "Integrations"),
          });
          if (section && sectionLabels[section]) {
            const projectPath = `/projects/${location.split('/')[2]}`;
            const named = (label: string) => pageTitle ?? label;
            /* A section with sub-pages (Availability → Lots or Suites, Home
               Library) names the open one too, so the trail says which half
               you are in. The sub-page is chosen by the next path segment;
               anything that is not a named child — that page's own views,
               table, floorplates, siteplan, 3d — belongs to the first. */
            const item = PROJECT_MENU_ITEMS.find((i) => i.url.split('/')[0] === section);
            if (item?.children?.length) {
              const sub = location.split(/[/?#]/)[4];
              const child = item.children.find((c) => c.url.split('/')[1] === sub) ?? item.children[0];
              crumbs.push({ label: sectionLabels[section], path: `${projectPath}/${section}` });
              // The page may name itself after the SECTION ("Availability"),
              // which would say the parent twice; then the child keeps its own.
              const label = navChildLabel(child, isHighRise);
              const own = pageTitle && pageTitle.toLowerCase() !== sectionLabels[section].toLowerCase() ? pageTitle : (label.titleKey ? t(label.titleKey, label.title) : label.title);
              crumbs.push({ label: own, path: `${projectPath}/${child.url}` });
            } else {
              crumbs.push({ label: named(sectionLabels[section]), path: location });
            }
          }
        }
      }
    }
    
    return crumbs;
  };
  
  const adminSections = useGlobalNav();
  const companySections = useCompanyNav();

  /*
    Platform Settings as a page (a phone or a portrait tablet — from the
    sidenav breakpoint up it is PlatformModal, which draws its own): PropPay's
    breadcrumb. The settings glyph, then the open section. On the section list
    the glyph says it alone — the page header names Platform Settings right
    beneath — and inside a section it is the way back to that list, in the
    theme ink because it is a link.
  */
  if (isAdmin && isAdminPath(normalizedLoc)) {
    return (
      <AreaCrumb
        glyph={MonitorCog}
        label={t("platformSettings", "Platform Settings") || "Platform Settings"}
        home="/admin"
        section={adminSectionFor(normalizedLoc, adminSections)?.title}
      />
    );
  }

  /* Company Settings as a page, the same way (Andrew, 2026-09-28): the
     company glyph — the one the sidebar and tab bar give Company Settings —
     then the section. On the list at `/company-admin` the glyph stands alone;
     `?projectId=` rides along on the way back to it. */
  if (canAccessCompanySettings(user) && isCompanyPath(normalizedLoc)) {
    const onList = normalizedLoc === '/company-admin';
    const projectParam = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('projectId');
    return (
      <AreaCrumb
        glyph={BriefcaseBusiness}
        label={t("companySettings", "Company Settings") || "Company Settings"}
        home={projectParam ? `/company-admin?projectId=${projectParam}` : '/company-admin'}
        section={onList ? undefined : companySectionFor(normalizedLoc, companySections)?.title}
      />
    );
  }

  const breadcrumbs = getBreadcrumbs();
  
  const onProjects = normalizedLoc === '/projects';

  /* PropPay's breadcrumb: the project grid first — in the theme ink while it
     is a link, plain on the Projects page itself — then the trail, the last
     step in bold. 18px glyph, 15px chevrons, the label rung throughout. */
  return (
    <nav aria-label={t("shellNavBreadcrumbLabel", "Breadcrumb")} className="flex min-w-0 items-center gap-2 overflow-x-auto overflow-y-hidden text-label text-muted-foreground scrollbar-hide">
      {breadcrumbs.map((crumb, index) => {
        const last = index === breadcrumbs.length - 1;
        if (index === 0) {
          return onProjects || (last && shouldHideBreadcrumb) ? (
            <span key={crumb.path} role="img" aria-current={onProjects ? "page" : undefined} aria-label={t("projectsTitle", "Projects")} className="flex shrink-0 items-center text-foreground">
              <LayoutGrid className="size-[18px]" aria-hidden="true" />
            </span>
          ) : (
            <Tooltip key={crumb.path}>
              <TooltipTrigger asChild>
                <Link
                  href={crumb.path}
                  aria-label={t("shellSpacesAll", "All Projects")}
                  // In the flat shell, the header's own ink, as its other icons: colour there means "selected".
                  className={cn("hit-target flex shrink-0 items-center transition-colors duration-instant ease-standard hover:text-foreground", isFlatShell() ? "text-foreground/70" : "text-primary-ink")}
                >
                  <LayoutGrid className="size-[18px]" aria-hidden="true" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("shellSpacesAll", "All Projects")}</TooltipContent>
            </Tooltip>
          );
        }
        /* On a phone, PropPay's crumb: the grid, then the PROJECT in bold as
           a link back to its dashboard — the page below says which screen you
           are on, and a 402px bar has room for one name, not a trail. From
           640px the full trail returns. */
        const beyondProject = index >= 2;
        const projectOnPhone = index === 1 && !last;
        /* When the bar runs short (an iPad in portrait, with a project name
           like "A0702 Countryside Villages Phase 3"), the PROJECT gives way
           first: the section and the page you are on are short words and
           the whole point of the trail, and truncating all four at once
           read "Harbourview Te… › Availa… › Inven…".

           It gives way at a FIXED share of the bar, not whatever the page's
           name leaves it. Shrinking to fit made the project's width depend on
           the page — "Lafontaine — Pha…" on Dashboard, "Lafontaine — Phase…"
           on Contacts — so the chevron and the page name slid sideways on
           every navigation (12–64px on an iPad, measured by shift-audit's
           chrome-nav pass, 2026-10-27). Nav never moves between pages. */
        const yields = index === 1 && !last;
        return (
          <div
            key={crumb.path}
            className={cn(
              "items-center gap-2",
              // The project alone gives way; past it every crumb is a short
              // section name kept whole. The project itself as the last crumb
              // (its dashboard) still truncates.
              // 35% leaves a portrait iPad room for a two-level trail
              // ("Availability › Suites") whole, so the project holds still on
              // every page; a trail that still cannot fit squeezes it, down to
              // 3rem. A phone shows the project alone, so no cap below sm.
              yields ? "min-w-[3rem] shrink sm:max-w-[35%]" : index === 1 ? "min-w-0" : "shrink-0",
              beyondProject ? "hidden sm:flex" : "flex",
            )}
          >
            <ChevronRight className="size-[15px] shrink-0 opacity-60" aria-hidden="true" />
            {last ? (
              <span aria-current="page" className="truncate font-bold text-foreground">{crumb.label}</span>
            ) : (
              <Link
                href={crumb.path}
                className={cn(
                  "truncate transition-colors duration-instant ease-standard hover:text-foreground",
                  projectOnPhone && "font-bold text-foreground coarse:py-3.5 sm:font-normal sm:text-inherit",
                )}
              >
                {crumb.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}

/**
 * A settings area's breadcrumb on a phone: its glyph, then the open section.
 * On the area's section list the glyph says it alone — the page header names
 * the area right beneath — and inside a section it is the way back to that
 * list, in the theme ink because it is a link.
 */
function AreaCrumb({ glyph: Glyph, label, home, section }: { glyph: LucideIcon; label: string; home: string; section?: string }) {
  const t = useTranslation();
  const glyph = <Glyph className="size-[18px]" aria-hidden="true" />;
  /* The glyph alone, at every depth (Andrew, 2026-09-29). Inside a section it
     is a link back to the area's list, in the brand ink; on the list itself it
     is plain. The section's name is not repeated here: the page header right
     below names it, and its eyebrow is the way back (PageHeader's
     SettingsEyebrow) — a "› Users" here was the same word 60px above the title. */
  return (
    <nav aria-label={t("shellNavBreadcrumbLabel", "Breadcrumb")} className="flex min-w-0 items-center gap-2 text-label text-muted-foreground">
      {section ? (
        <Link
          href={home}
          aria-label={t("shellNavBackTo", "Back to {area}", { area: label })}
          className="flex shrink-0 items-center text-primary-ink transition-colors hover:text-foreground hit-target"
        >
          {glyph}
        </Link>
      ) : (
        <span className="flex shrink-0 items-center text-foreground" role="img" aria-label={label}>{glyph}</span>
      )}
    </nav>
  );
}
